/** Classified fixed-kind tab inputs only; no response/provider material. */
import type { CompanyPurchaseCommandIntent } from "../api/companyPurchaseContracts";
import type { CompanySoftwareBillingScope } from "../api/companySoftwareBilling";
import { sameCompanyBillingScope } from "./companyMethodIntent";

type Kind = CompanyPurchaseCommandIntent["kind"];
const kinds: readonly Kind[] = ["purchase", "stop_renewal", "restore_renewal"];
const key = (kind: Kind) => `bw:company-purchase-intent:v1:${kind}`;
const uuid = (v: unknown) => typeof v === "string" && /^(?!00000000-0000-0000-0000-000000000000$)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const hash = (v: unknown) => typeof v === "string" && /^[a-f0-9]{64}$/.test(v);
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const keys = (v: Record<string, unknown>, expected: string[]) => Object.keys(v).length === expected.length && expected.every(k => Object.prototype.hasOwnProperty.call(v, k));

function valid(value: unknown): value is CompanyPurchaseCommandIntent {
  if (!object(value) || !keys(value, ["version", "scope", "kind", "request"]) || value.version !== 1
    || !object(value.scope) || !object(value.request) || !kinds.some(kind => kind === value.kind)) return false;
  const scope = value.scope, request = value.request;
  if (!keys(scope, ["actorId", "orgId", "acceptanceId", "environment"]) || typeof scope.actorId !== "string"
    || !scope.actorId.trim() || scope.actorId.length > 255 || !uuid(scope.orgId) || !uuid(scope.acceptanceId)
    || !["sandbox", "production"].includes(String(scope.environment)) || request.environment !== scope.environment || !uuid(request.command_id)) return false;
  if (value.kind === "purchase") return keys(request, ["environment", "command_id", "acceptance_id", "reviewed_terms_fingerprint", "reviewed_method_revision", "reviewed_review_fingerprint", "reviewed_lane_revision"])
    && request.acceptance_id === scope.acceptanceId && hash(request.reviewed_terms_fingerprint) && hash(request.reviewed_review_fingerprint)
    && Number.isSafeInteger(request.reviewed_method_revision) && Number(request.reviewed_method_revision) > 0
    && Number.isSafeInteger(request.reviewed_lane_revision) && Number(request.reviewed_lane_revision) >= 0;
  return keys(request, ["environment", "command_id", "purchase_id", "expected_licence_revision", "action"])
    && uuid(request.purchase_id) && request.action === value.kind && Number.isSafeInteger(request.expected_licence_revision)
    && Number(request.expected_licence_revision) > 0;
}
function read(kind: Kind): unknown {
  try { const raw = sessionStorage.getItem(key(kind)); return raw === null ? null : JSON.parse(raw); } catch { return null; }
}
function remove(kind: Kind): void { try { sessionStorage.removeItem(key(kind)); } catch { /* No financial state changes. */ } }

export function readCompanyPurchaseIntent(scope: CompanySoftwareBillingScope, kind: Kind): CompanyPurchaseCommandIntent | null {
  const value = read(kind);
  return valid(value) && value.kind === kind && sameCompanyBillingScope(value.scope, scope) ? value : null;
}
export function writeCompanyPurchaseIntent(value: CompanyPurchaseCommandIntent): boolean {
  if (!valid(value)) return false;
  const old = read(value.kind);
  // A stale scope/command callback cannot replace another unresolved original.
  if (old !== null && (!valid(old) || JSON.stringify(old) !== JSON.stringify(value))) return false;
  try { sessionStorage.setItem(key(value.kind), JSON.stringify(value)); return true; } catch { return false; }
}
export function clearCompanyPurchaseIntent(value: CompanyPurchaseCommandIntent): void {
  const old = read(value.kind);
  if (valid(old) && old.kind === value.kind && sameCompanyBillingScope(old.scope, value.scope)
    && old.request.command_id === value.request.command_id) remove(value.kind);
}
export function clearCompanyPurchaseIntentsForScope(scope: CompanySoftwareBillingScope): void {
  for (const kind of kinds) { const value = read(kind); if (valid(value) && sameCompanyBillingScope(value.scope, scope)) remove(kind); }
}
export function synchronizeCompanyPurchaseIntentActor(actorId: string | null): void {
  for (const kind of kinds) { const value = read(kind); if (actorId === null || !valid(value) || value.kind !== kind || value.scope.actorId !== actorId) remove(kind); }
}
export function synchronizeCompanyPurchaseIntentScope(scope: CompanySoftwareBillingScope): void {
  for (const kind of kinds) { const value = read(kind); if (!valid(value) || value.kind !== kind || !sameCompanyBillingScope(value.scope, scope)) remove(kind); }
}
